import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery } from 'react-query';
import { api } from '../http';
import { DefaultRes } from '../types';
import { useDebounce } from './useDebounce';

interface UserSearchResult {
    _id: string;
    id?: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    backupEmail?: string;
    teamId?: string;
    employeeId?: string;
    appStatus?: 'ON' | 'OFF';
}

interface PaginationInfo {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

interface UserSearchResponse {
    items: UserSearchResult[];
    page: number;
    perPage: number;
    total: number;
}

interface UseUserSearchOptions {
    /** IDs of users to exclude from search results (e.g., already selected) */
    excludeIds?: string[];
    /** Number of users to fetch per page */
    perPage?: number;
    /** Debounce delay in milliseconds */
    debounceMs?: number;
    /** Whether to enable the query */
    enabled?: boolean;
}

/**
 * Hook for paginated, searchable user selection
 * Uses the mobile-app-users endpoint with infinite scroll support
 */
export function useUserSearch(options: UseUserSearchOptions = {}) {
    const {
        excludeIds = [],
        perPage = 50,
        debounceMs = 300,
        enabled = true,
    } = options;

    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebounce(searchInput, debounceMs);

    const fetchUsers = useCallback(async ({ pageParam = 1 }): Promise<UserSearchResponse> => {
        const params = new URLSearchParams({
            page: String(pageParam),
            perPage: String(perPage),
            sortBy: 'firstName',
            sortOrder: 'asc',
            includeTeamId: 'true',
        });

        if (debouncedSearch) {
            params.append('filter[search]', debouncedSearch);
        }

        const { data } = await api.get<DefaultRes<UserSearchResponse>>(
            `/mobile-app-users?${params.toString()}`
        );

        return data.result;
    }, [debouncedSearch, perPage]);

    const query = useInfiniteQuery(
        ['userSearch', debouncedSearch, perPage],
        fetchUsers,
        {
            getNextPageParam: (lastPage) => {
                const currentPage = lastPage.page;
                const totalPages = Math.ceil(lastPage.total / lastPage.perPage);
                return currentPage < totalPages ? currentPage + 1 : undefined;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes cache
            cacheTime: 10 * 60 * 1000, // 10 minutes
            enabled,
            refetchOnWindowFocus: false,
        }
    );

    // Flatten all pages and exclude already-selected users
    const users = useMemo(() => {
        if (!query.data?.pages) return [];

        const allUsers = query.data.pages.flatMap(page => page.items);

        // Normalize IDs and filter out excluded users
        return allUsers
            .map(user => ({
                ...user,
                // Normalize _id to id for consistency
                id: user.id || user._id,
            }))
            .filter(user => !excludeIds.includes(user.id || user._id));
    }, [query.data?.pages, excludeIds]);

    // Total count from first page
    const totalCount = query.data?.pages[0]?.total ?? 0;

    // Handle scroll to bottom - load more
    const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
        const isNearBottom = scrollHeight - scrollTop <= clientHeight + 100;

        if (isNearBottom && query.hasNextPage && !query.isFetchingNextPage) {
            query.fetchNextPage();
        }
    }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

    return {
        /** Search input value */
        searchInput,
        /** Set search input value (will be debounced) */
        setSearchInput,
        /** Debounced search term currently being used */
        searchTerm: debouncedSearch,
        /** Filtered users (excluding already selected) */
        users,
        /** Total count of matching users */
        totalCount,
        /** Loading state for initial load */
        isLoading: query.isLoading,
        /** Loading state for fetching next page */
        isFetchingNextPage: query.isFetchingNextPage,
        /** Whether there are more pages to load */
        hasNextPage: query.hasNextPage ?? false,
        /** Function to load next page */
        fetchNextPage: query.fetchNextPage,
        /** Error state */
        error: query.error,
        /** Scroll handler for infinite scroll - attach to scrollable container */
        handleScroll,
        /** Refetch the query */
        refetch: query.refetch,
    };
}

export type { UserSearchResult, UseUserSearchOptions };
